'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircleIcon } from '@phosphor-icons/react';
import { Input } from '@/components/seasons/Input';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Heading displayed at the top of the panel. */
  title?: string;
  /** One-line context under the heading. */
  description?: string;
  /** Label for the submit button. */
  submitLabel?: string;
  /** Hidden form_source field sent to HubSpot — useful for routing in HubSpot. */
  source?: string;
}

interface FormState {
  firstName: string;
  lastName: string;
  organization: string;
  email: string;
}

const EASE_OUT: [number, number, number, number] = [0, 0, 0.2, 1];
const EASE_IN_OUT: [number, number, number, number] = [0.4, 0, 0.2, 1];

const HS_PORTAL_ID = '14536254';
const HS_BLOG_FORM_GUID = '81f554c8-064e-4d32-a1b4-004780b921e1';

async function submitToHubspot(email: string) {
  try {
    const { ip } = await fetch('/api/get-ip').then(r => r.json()).catch(() => ({ ip: '' }));
    await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HS_PORTAL_ID}/${HS_BLOG_FORM_GUID}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: [
            { name: 'email',     value: email },
            { name: 'collateral_title', value: document.title },
            { name: 'collateral',       value: window.location.href },
            { name: 'collateral_type',  value: 'Stories' },
          ],
          // HubSpot rejects the whole submission with INVALID_IP_ADDRESS if
          // ipAddress is present but empty — omit the key instead of sending ''.
          context: { pageUri: window.location.href, pageName: document.title, ...(ip ? { ipAddress: ip } : {}) },
        }),
      }
    );
  } catch { /* silent */ }
}

const INITIAL: FormState = {
  firstName: '',
  lastName: '',
  organization: '',
  email: '',
};

export function SubscribeSlideOver({
  open,
  onClose,
  title = "Like what you're reading?",
  description = 'Get more insights on healthcare innovation, AI, and value-based care from Innovaccer.',
  submitLabel = 'Submit',
  source = 'nav-subscribe',
}: Props) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const [hiddenFields, setHiddenFields] = useState({ pageName: '', pageURI: '', pageID: '', ipAddress: '', hutk: '' });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const hutk = document.cookie.split('; ').find(r => r.startsWith('hubspotutk='))?.split('=')[1] ?? '';
    fetch('/api/get-ip').then(r => r.json()).catch(() => ({ ip: '' })).then(({ ip }) => {
      setHiddenFields({
        pageName: document.title,
        pageURI: window.location.href,
        pageID: window.location.pathname,
        ipAddress: ip ?? '',
        hutk,
      });
    });
  }, []);

  // Esc to close, focus trap to first field on open, body scroll lock
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    setTimeout(() => {
      const first = document.getElementById('sso-first') as HTMLInputElement | null;
      first?.focus();
    }, 60);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // Reset form when the panel re-opens after a successful submit
  useEffect(() => {
    if (open && submitted) {
      const t = setTimeout(() => {
        setSubmitted(false);
        setForm(INITIAL);
      }, 400);
      return () => clearTimeout(t);
    }
  }, [open, submitted]);

  function update<K extends keyof FormState>(key: K) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
      // Submit to HubSpot in background — don't block success state
      submitToHubspot(form.email);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="subscribe-slideover" role="dialog" aria-modal="true" aria-labelledby="subscribe-slideover-title">
          {/* Backdrop */}
          <motion.div
            className="subscribe-slideover__backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Sliding panel */}
          <motion.div
            ref={panelRef}
            className="subscribe-slideover__panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.45, ease: EASE_IN_OUT }}
          >
            <button
              type="button"
              className="btn-icon-only btn-icon-only--md btn-icon-only--ghost subscribe-slideover__close"
              aria-label="Close"
              onClick={onClose}
            >
              <X size={18} weight="bold" />
            </button>

            {submitted ? (
              <div className="subscribe-slideover__success">
                <CheckCircleIcon size={56} weight="fill" style={{ color: 'var(--seasons-color-cyber-blue)' }} />
                <h2 className="type-heading-xl" style={{ marginTop: 'var(--seasons-space-4)' }}>
                  Thanks — we’ll be in touch
                </h2>
                <p
                  className="type-body-md"
                  style={{ color: 'var(--seasons-color-text-secondary)', marginTop: 'var(--seasons-space-2)', maxWidth: 380 }}
                >
                  A member of our team will reach out within one business day. Close this panel to keep browsing.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  style={{ marginTop: 'var(--seasons-space-6)', background: 'transparent', border: 0, padding: 0, cursor: 'pointer' }}
                >
                  <span className="block-button block-button--md block-button--midnight">
                    <span className="block-button__text">
                      <span className="btn-primary-text">Close</span>
                      <span className="btn-hover-text">Close</span>
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <form className="subscribe-slideover__form" onSubmit={handleSubmit}>
                <input type="hidden" name="collateral_title" value={hiddenFields.pageName} />
                <input type="hidden" name="collateral"       value={hiddenFields.pageURI} />
                <input type="hidden" name="collateral_type"  value={hiddenFields.pageID} />
                <input type="hidden" name="ipAddress"        value={hiddenFields.ipAddress} />
                <input type="hidden" name="hutk"             value={hiddenFields.hutk} />
                <header className="subscribe-slideover__header">
                  <h2 id="subscribe-slideover-title" className="type-heading-xl">
                    {title}
                  </h2>
                  <p className="type-body-md" style={{ color: 'var(--seasons-color-text-secondary)', marginTop: 'var(--seasons-space-2)' }}>
                    {description}
                  </p>
                </header>

                <div className="subscribe-slideover__row">
                  <Input
                    id="sso-first"
                    label="First name"
                    required
                    value={form.firstName}
                    onChange={update('firstName')}
                  />
                  <Input id="sso-last" label="Last name" required value={form.lastName} onChange={update('lastName')} />
                </div>
                <Input
                  id="sso-org"
                  label="Organization"
                  required
                  value={form.organization}
                  onChange={update('organization')}
                />
                <Input
                  id="sso-email"
                  label="Work email"
                  type="email"
                  required
                  value={form.email}
                  onChange={update('email')}
                />
                {error && (
                  <p className="type-body-sm" style={{ color: 'var(--seasons-color-text-danger)' }}>
                    {error}
                  </p>
                )}

                <p className="type-body-sm subscribe-slideover__legal">
                  By submitting, you confirm that you agree to the processing of your personal data as described in the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Statement
                  </a>
                  .
                </p>

                <button
                  type="submit"
                  className="subscribe-slideover__submit"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  <span className={`block-button block-button--md block-button--midnight${submitting ? ' btn-loading' : ''}`}>
                    <span className="block-button__text">
                      <span className="btn-primary-text">{submitting ? 'Submitting…' : submitLabel}</span>
                      <span className="btn-hover-text">{submitting ? 'Submitting…' : submitLabel}</span>
                    </span>
                  </span>
                </button>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
